/* ROUX Header V3.0 */
/*
  ROUX Header
  Intended file: assets/roux-header.js
*/

(() => {
  'use strict';

  const SELECTOR = '[data-roux-header]';

  class RouxHeader {
    constructor(root) {
      this.root = root;
      this.fixedHeader = root.querySelector('[data-header-fixed]');
      this.overlay = root.querySelector('[data-overlay]');
      this.mobileMenu = root.querySelector('[data-mobile-menu]');
      this.searchPanel = root.querySelector('[data-search-panel]');
      this.cartDrawer = root.querySelector('[data-cart-drawer]');
      this.cartBody = root.querySelector('[data-cart-body]');
      this.cartFooter = root.querySelector('[data-cart-footer]');
      this.cartLoading = root.querySelector('[data-cart-loading]');
      this.liveRegion = root.querySelector('[data-live-region]');
      this.searchInput = root.querySelector('[data-search-input]');
      this.thresholdCents = Number(root.dataset.thresholdCents || 0);
      this.deliveryCents = Number(root.dataset.deliveryCents || 0);
      this.currency = root.dataset.currency || 'USD';
      this.progressTemplate = root.dataset.progressMessage || 'ADD [amount] MORE FOR FREE DELIVERY';
      this.freeMessage = root.dataset.freeMessage || 'YOU UNLOCKED FREE DELIVERY';
      this.cartOffersEnabled = root.dataset.cartOffersEnabled !== 'false';
      this.offerProductId = Number(root.dataset.offerProductId || 0);
      this.offerDiscountCents = Number(root.dataset.offerDiscountCents || 1000);
      this.activePanel = null;
      this.lastTrigger = null;
      this.cart = null;
      this.isUpdatingCart = false;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.bindEvents();
      this.handleScroll();
      this.refreshCart({ silent: true });
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => this.handleClick(event), { signal: this.signal });
      this.overlay?.addEventListener('click', () => this.closeAll(), { signal: this.signal });
      document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.signal });
      window.addEventListener('scroll', () => this.handleScroll(), { passive: true, signal: this.signal });

      document.addEventListener('roux:cart:refresh', (event) => {
        const shouldOpen = Boolean(event.detail?.open);
        this.refreshCart({ open: shouldOpen });
      }, { signal: this.signal });

      document.addEventListener('cart:refresh', () => this.refreshCart(), { signal: this.signal });
      document.addEventListener('cart:updated', () => this.refreshCart(), { signal: this.signal });
      document.addEventListener('product:added', () => this.refreshCart({ open: true }), { signal: this.signal });

      document.addEventListener('shopify:section:load', (event) => {
        if (event.target?.querySelector?.(SELECTOR) === this.root) {
          this.refreshCart({ silent: true });
        }
      }, { signal: this.signal });
    }

    handleClick(event) {
      const openMenu = event.target.closest('[data-menu-open]');
      const closeMenu = event.target.closest('[data-menu-close]');
      const openSearch = event.target.closest('[data-search-open]');
      const closeSearch = event.target.closest('[data-search-close]');
      const openCart = event.target.closest('[data-cart-open]');
      const closeCart = event.target.closest('[data-cart-close]');
      const continueShopping = event.target.closest('[data-cart-continue]');
      const qtyMinus = event.target.closest('[data-qty-minus]');
      const qtyPlus = event.target.closest('[data-qty-plus]');
      const remove = event.target.closest('[data-cart-remove]');
      const cartOffer = event.target.closest('[data-cart-offer-add]');

      if (openMenu) {
        event.preventDefault();
        this.openPanel(this.mobileMenu, openMenu);
        return;
      }

      if (closeMenu) {
        this.closeAll();
        return;
      }

      if (openSearch) {
        event.preventDefault();
        this.openPanel(this.searchPanel, openSearch);
        window.setTimeout(() => this.searchInput?.focus(), 320);
        return;
      }

      if (closeSearch) {
        this.closeAll();
        return;
      }

      if (openCart) {
        event.preventDefault();
        this.openPanel(this.cartDrawer, openCart);
        this.refreshCart({ silent: true });
        return;
      }

      if (closeCart || continueShopping) {
        this.closeAll();
        return;
      }

      if (cartOffer) {
        event.preventDefault();
        if (this.isUpdatingCart) return;
        const variantId = Number(cartOffer.dataset.variantId || 0);
        const quantity = Math.max(1, Number(cartOffer.dataset.addQuantity || 1));
        this.addOfferQuantity(variantId, quantity);
        return;
      }

      if (qtyMinus || qtyPlus || remove) {
        const item = event.target.closest('[data-line-key]');
        if (!item || this.isUpdatingCart) return;

        const key = item.dataset.lineKey;
        const quantityEl = item.querySelector('[data-line-quantity]');
        const currentQty = Number(quantityEl?.textContent || 1);

        if (remove) {
          this.changeLine(key, 0);
          return;
        }

        const nextQty = qtyMinus ? Math.max(0, currentQty - 1) : currentQty + 1;
        this.changeLine(key, nextQty);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.activePanel) {
        event.preventDefault();
        this.closeAll();
        return;
      }

      if (event.key === 'Tab' && this.activePanel) {
        this.trapFocus(event, this.activePanel);
      }
    }

    handleScroll() {
      if (!this.fixedHeader) return;
      this.fixedHeader.classList.toggle('is-scrolled', window.scrollY > 12);
    }

    openPanel(panel, trigger) {
      if (!panel) return;

      if (this.activePanel && this.activePanel !== panel) {
        this.activePanel.classList.remove('is-open');
        this.activePanel.setAttribute('aria-hidden', 'true');
        this.updateTriggerState(this.activePanel, false);
      }

      this.lastTrigger = trigger || null;
      this.activePanel = panel;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      this.updateTriggerState(panel, true);

      if (this.overlay) {
        this.overlay.hidden = false;
        requestAnimationFrame(() => this.overlay.classList.add('is-active'));
      }

      document.body.classList.add('roux-panel-open');

      window.setTimeout(() => {
        const firstFocusable = this.getFocusable(panel)[0];
        firstFocusable?.focus({ preventScroll: true });
      }, 390);
    }

    closeAll({ restoreFocus = true } = {}) {
      if (!this.activePanel) return;

      const closingPanel = this.activePanel;
      closingPanel.classList.remove('is-open');
      closingPanel.setAttribute('aria-hidden', 'true');
      this.updateTriggerState(closingPanel, false);
      this.activePanel = null;

      this.overlay?.classList.remove('is-active');
      document.body.classList.remove('roux-panel-open');

      window.setTimeout(() => {
        if (this.overlay && !this.activePanel) this.overlay.hidden = true;
      }, 310);

      if (restoreFocus && this.lastTrigger) {
        this.lastTrigger.focus({ preventScroll: true });
      }
      this.lastTrigger = null;
    }

    updateTriggerState(panel, expanded) {
      if (!panel) return;
      let selector = null;
      if (panel === this.mobileMenu) selector = '[data-menu-open]';
      if (panel === this.searchPanel) selector = '[data-search-open]';
      if (panel === this.cartDrawer) selector = '[data-cart-open]';
      if (!selector) return;
      this.root.querySelector(selector)?.setAttribute('aria-expanded', String(expanded));
    }

    getFocusable(container) {
      if (!container) return [];
      return Array.from(container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
    }

    trapFocus(event, container) {
      const focusable = this.getFocusable(container);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async refreshCart({ open = false, silent = false } = {}) {
      try {
        if (!silent) this.setCartLoading(true);
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });

        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        const cart = await response.json();
        this.cart = cart;
        this.currency = cart.currency || this.currency;
        this.renderCart(cart);

        if (open) {
          const trigger = this.root.querySelector('[data-cart-open]');
          this.openPanel(this.cartDrawer, trigger);
        }

        return cart;
      } catch (error) {
        console.error('[ROUX Header] Unable to refresh cart.', error);
        this.announce('Unable to update your bag. Please try again.');
        return null;
      } finally {
        if (!silent) this.setCartLoading(false);
      }
    }

    async changeLine(key, quantity) {
      if (!key || this.isUpdatingCart) return;

      this.isUpdatingCart = true;
      this.setCartLoading(true);

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity })
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.description || data?.message || 'Unable to update cart.');
        }

        this.cart = data;
        this.currency = data.currency || this.currency;
        this.renderCart(data);
        this.announce(quantity === 0 ? 'Item removed from your bag.' : 'Bag updated.');
        document.dispatchEvent(new CustomEvent('roux:cart:updated', { detail: { cart: data } }));
      } catch (error) {
        console.error('[ROUX Header] Unable to change cart line.', error);
        this.announce(error.message || 'Unable to update your bag.');
        await this.refreshCart({ silent: true });
      } finally {
        this.isUpdatingCart = false;
        this.setCartLoading(false);
      }
    }

    async addOfferQuantity(variantId, quantity) {
      if (!variantId || quantity < 1 || this.isUpdatingCart) return;

      this.isUpdatingCart = true;
      this.setCartLoading(true);

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{ id: variantId, quantity }]
          })
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.description || data?.message || 'Unable to add this offer.');
        }

        const cart = await this.refreshCart({ silent: true });
        this.announce(quantity === 1 ? 'One more ROUX added to your bag.' : `${quantity} more ROUX added to your bag.`);
        if (cart) {
          document.dispatchEvent(new CustomEvent('roux:cart:updated', { detail: { cart } }));
        }
      } catch (error) {
        console.error('[ROUX Header] Unable to add cart offer.', error);
        this.announce(error.message || 'Unable to add this offer. Please try again.');
      } finally {
        this.isUpdatingCart = false;
        this.setCartLoading(false);
      }
    }

    renderCart(cart) {
      if (!cart || !this.cartBody) return;

      const count = Number(cart.item_count || 0);
      const countEls = this.root.querySelectorAll('[data-cart-count]');
      const headingCount = this.root.querySelector('[data-cart-heading-count]');
      const cartTrigger = this.root.querySelector('[data-cart-open]');

      countEls.forEach((el) => {
        el.textContent = String(count);
        el.classList.toggle('is-empty', count === 0);
      });

      if (headingCount) headingCount.textContent = String(count);
      if (cartTrigger) cartTrigger.setAttribute('aria-label', `Open cart, ${count} ${count === 1 ? 'item' : 'items'}`);

      const headerStrong = this.cartDrawer?.querySelector('.roux-cart-drawer__header strong');
      if (headerStrong) {
        headerStrong.innerHTML = `<span data-cart-heading-count>${count}</span> ${count === 1 ? 'ITEM' : 'ITEMS'}`;
      }

      if (count === 0) {
        this.cartBody.innerHTML = this.emptyCartMarkup();
        this.cartFooter?.classList.add('is-hidden');
      } else {
        this.cartBody.innerHTML = `<div class="roux-cart-items" data-cart-items>${cart.items.map((item) => this.itemMarkup(item)).join('')}</div>${this.cartOffersMarkup(cart)}`;
        this.cartFooter?.classList.remove('is-hidden');
      }

      this.updateProgress(cart);
      this.updateFooter(cart);
    }

    cartOffersMarkup(cart) {
      if (!this.cartOffersEnabled || !cart?.items?.length) return '';

      const configuredProductId = Number(this.offerProductId || 0);
      const matchingItems = configuredProductId
        ? cart.items.filter((item) => Number(item.product_id) === configuredProductId)
        : [cart.items[0]];

      if (!matchingItems.length) return '';

      const productQuantity = matchingItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (productQuantity < 1 || productQuantity >= 3) return '';

      const variantId = Number(matchingItems[0]?.variant_id || 0);
      if (!variantId) return '';

      const discountText = this.formatMoney(this.offerDiscountCents);
      const offers = [];

      if (productQuantity === 1) {
        offers.push(this.cartOfferCardMarkup({
          variantId,
          addQuantity: 1,
          title: 'ADD 1 MORE',
          detail: 'Make it 2 and unlock free delivery.',
          action: '+1',
          best: false,
          shake: true
        }));

        offers.push(this.cartOfferCardMarkup({
          variantId,
          addQuantity: 2,
          title: 'ADD 2 MORE',
          detail: `Make it 3 for free delivery + ${discountText} off.`,
          action: '+2',
          best: true
        }));
      } else if (productQuantity === 2) {
        offers.push(this.cartOfferCardMarkup({
          variantId,
          addQuantity: 1,
          title: 'ADD 1 MORE',
          detail: `Make it 3 and unlock ${discountText} off.`,
          action: '+1',
          best: true,
          shake: true
        }));
      }

      if (!offers.length) return '';

      return `
        <div class="roux-cart-offers" data-cart-offers>
          <span class="roux-cart-offers__eyebrow">OPTIONAL SAVINGS</span>
          <div class="roux-cart-offers__list">${offers.join('')}</div>
        </div>
      `;
    }

    cartOfferCardMarkup({ variantId, addQuantity, title, detail, action, best, shake = false }) {
      return `
        <button
          type="button"
          class="roux-cart-offer${best ? ' roux-cart-offer--best' : ''}${shake ? ' roux-cart-offer--shake' : ''}"
          data-cart-offer-add
          data-variant-id="${Number(variantId)}"
          data-add-quantity="${Number(addQuantity)}"
          aria-label="${this.escapeAttribute(`${title}. ${detail}`)}"
        >
          <span class="roux-cart-offer__copy">
            <span class="roux-cart-offer__title">${this.escapeHtml(title)}</span>
            <span class="roux-cart-offer__detail">${this.escapeHtml(detail)}</span>
          </span>
          <span class="roux-cart-offer__action" aria-hidden="true">${this.escapeHtml(action)}</span>
        </button>
      `;
    }

    itemMarkup(item) {
      const image = item.featured_image?.url || item.image || '';
      const imageAlt = item.featured_image?.alt || item.product_title || '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="roux-cart-item__variant">${this.escapeHtml(item.variant_title)}</div>`
        : '';

      const imageMarkup = image
        ? `<img src="${this.escapeAttribute(this.withImageWidth(image, 240))}" alt="${this.escapeAttribute(imageAlt)}" loading="lazy" width="240" height="280">`
        : '<div aria-hidden="true"></div>';

      return `
        <article class="roux-cart-item" data-line-key="${this.escapeAttribute(item.key)}">
          <a href="${this.escapeAttribute(item.url || '#')}" class="roux-cart-item__image" tabindex="-1" aria-hidden="true">
            ${imageMarkup}
          </a>
          <div class="roux-cart-item__content">
            <div class="roux-cart-item__top">
              <div>
                <a href="${this.escapeAttribute(item.url || '#')}" class="roux-cart-item__title">${this.escapeHtml(item.product_title || item.title || 'Product')}</a>
                ${variant}
              </div>
              <button type="button" class="roux-cart-item__remove" aria-label="Remove ${this.escapeAttribute(item.product_title || 'item')}" data-cart-remove>REMOVE</button>
            </div>
            <div class="roux-cart-item__bottom">
              <div class="roux-quantity" role="group" aria-label="Quantity for ${this.escapeAttribute(item.product_title || 'item')}">
                <button type="button" aria-label="Decrease quantity" data-qty-minus>−</button>
                <span data-line-quantity>${Number(item.quantity || 0)}</span>
                <button type="button" aria-label="Increase quantity" data-qty-plus>+</button>
              </div>
              <span class="roux-cart-item__price">${this.formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </article>
      `;
    }

    emptyCartMarkup() {
      return `
        <div class="roux-cart-empty" data-cart-empty>
          <span class="roux-cart-empty__mark">R</span>
          <h2>YOUR BAG IS EMPTY</h2>
          <p>Discover effortless texture, volume and a natural matte finish.</p>
          <button type="button" class="roux-button roux-button--primary" data-cart-continue>CONTINUE SHOPPING</button>
        </div>
      `;
    }

    updateProgress(cart) {
      const subtotal = Number(cart.total_price || 0);
      const bar = this.root.querySelector('[data-shipping-bar]');
      const message = this.root.querySelector('[data-shipping-message]');
      const deliveryPrice = this.root.querySelector('[data-delivery-price]');
      const nextReward = this.root.querySelector('[data-shipping-next-reward]');

      const configuredProductId = Number(this.offerProductId || 0);
      const rewardItems = configuredProductId
        ? (cart.items || []).filter((item) => Number(item.product_id) === configuredProductId)
        : ((cart.items || []).length ? [cart.items[0]] : []);
      const offerQuantity = rewardItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      const freeDeliveryUnlocked = subtotal >= this.thresholdCents || offerQuantity >= 2;
      const progress = freeDeliveryUnlocked
        ? 100
        : this.thresholdCents > 0
          ? Math.min(100, Math.max(0, (subtotal / this.thresholdCents) * 100))
          : 100;

      if (bar) bar.style.width = `${progress}%`;

      if (message) {
        if (freeDeliveryUnlocked) {
          message.textContent = this.freeMessage;
        } else {
          const remaining = Math.max(0, this.thresholdCents - subtotal);
          message.textContent = this.progressTemplate.replace('[amount]', this.formatMoney(remaining));
        }
      }

      if (deliveryPrice) {
        deliveryPrice.textContent = `${this.formatMoney(this.deliveryCents)} / FREE OVER ${this.formatMoney(this.thresholdCents)}`;
      }

      if (nextReward) {
        const showNextReward = this.cartOffersEnabled && offerQuantity === 2 && this.offerDiscountCents > 0;
        nextReward.classList.toggle('is-hidden', !showNextReward);
        if (showNextReward) {
          nextReward.querySelector('span:last-child').textContent = `NEXT REWARD • ADD 1 MORE & SAVE ${this.formatMoney(this.offerDiscountCents)}`;
        }
      }
    }

    updateFooter(cart) {
      const subtotal = Number(cart.total_price || 0);
      const configuredProductId = Number(this.offerProductId || 0);
      const deliveryRewardItems = configuredProductId
        ? (cart.items || []).filter((item) => Number(item.product_id) === configuredProductId)
        : ((cart.items || []).length ? [cart.items[0]] : []);
      const offerQuantity = deliveryRewardItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const freeDeliveryUnlocked = subtotal >= this.thresholdCents || offerQuantity >= 2;
      const deliveryDue = freeDeliveryUnlocked ? 0 : this.deliveryCents;
      const totalWithDelivery = subtotal + deliveryDue;
      const subtotalEl = this.root.querySelector('[data-cart-subtotal]');
      const deliveryEl = this.root.querySelector('[data-cart-delivery-amount]');
      const totalEl = this.root.querySelector('[data-cart-total]');
      const note = this.root.querySelector('[data-cart-delivery-note]');

      if (subtotalEl) subtotalEl.textContent = this.formatMoney(subtotal);
      if (deliveryEl) deliveryEl.textContent = deliveryDue > 0 ? this.formatMoney(deliveryDue) : 'FREE';
      if (totalEl) totalEl.textContent = this.formatMoney(totalWithDelivery);

      if (note) {
        note.textContent = deliveryDue === 0
          ? 'FREE delivery across Lebanon.'
          : `${this.formatMoney(deliveryDue)} delivery is included in the total above. Add more to unlock free delivery.`;
      }
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.currency || 'USD',
          minimumFractionDigits: value % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }).format(value);
      } catch (_error) {
        return `$${value.toFixed(value % 1 === 0 ? 0 : 2)}`;
      }
    }

    withImageWidth(url, width) {
      if (!url) return '';
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', String(width));
        return parsed.toString();
      } catch (_error) {
        return url;
      }
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replaceAll('`', '&#096;');
    }

    setCartLoading(active) {
      this.cartLoading?.classList.toggle('is-active', Boolean(active));
      this.cartLoading?.setAttribute('aria-hidden', active ? 'false' : 'true');
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      window.setTimeout(() => {
        this.liveRegion.textContent = message;
      }, 20);
    }

    destroy() {
      this.abortController.abort();
      this.closeAll({ restoreFocus: false });
    }
  }

  const instances = new Map();

  const init = (scope = document) => {
    scope.querySelectorAll?.(SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new RouxHeader(root));
    });
  };

  const destroyWithin = (scope) => {
    scope.querySelectorAll?.(SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  window.RouxCartDrawer = {
    refresh(options = {}) {
      const first = instances.values().next().value;
      return first?.refreshCart(options) || Promise.resolve(null);
    },
    open() {
      const first = instances.values().next().value;
      if (!first) return;
      const trigger = first.root.querySelector('[data-cart-open]');
      first.openPanel(first.cartDrawer, trigger);
      first.refreshCart({ silent: true });
    },
    close() {
      const first = instances.values().next().value;
      first?.closeAll();
    },
    refreshAndOpen() {
      const first = instances.values().next().value;
      return first?.refreshCart({ open: true }) || Promise.resolve(null);
    }
  };
})();
